import { useMemo, useState, useEffect } from 'react';

import { Flex } from 'components/flex';
import { Icon } from 'components/icon';
import { Select } from 'components/select';
import { Dialog } from 'components/dialog';
import { Loader } from 'components/loader';
import { Button } from 'components/button';
import { formatPrice } from 'utils/helpers';
import { Adjustment, PayoutType } from 'types';
import { TitleLevel, ButtonColors, ExpensesType } from 'types/enums';
import { CreateBonusAdvanceDialog } from 'components/create-bonus-advance-dialog';
import { useListQuery as usePaymentMethodsQuery } from 'store/api/payment-methods';

import * as Styles from './styles';
import * as SubStyles from './subrows';
import { PaycheckDialogProps } from './types';
import { Card, AdjustmentCard } from './card';
import { ManualPayoutRows } from './manual-rows';
import {
  boldTypes,
  minusTypes,
  getPayoutItemsForAmount,
  getTransformedPaychecks,
  getPayoutPaymentMethodOptions,
  buildDisplayChargebackAdjustments,
} from './helpers';

const customTypeValues = [
  ExpensesType.Bonus,
  ExpensesType.Expense,
  ExpensesType.Reimbursement,
  ExpensesType.Advance,
  ExpensesType.Misc,
];

type SelectedPaymentMethod = {
  id?: number;
  eventId?: number;
};

export const PaycheckDialog = ({
  data,
  onPay,
  onClose,
  employee,
  isLoading,
  onRefresh,
  selectedEvent,
}: PaycheckDialogProps) => {
  const [paymentMethod, setPaymentMethod] = useState<SelectedPaymentMethod>({});
  const [payoutAmount, setPayoutAmount] = useState('');
  const [correcting, setCorrecting] = useState<string>();
  const [adding, setAdding] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState('');
  const refresh = async () => {
    if (!onRefresh) {
      return;
    }
    setRefreshing(true);
    setRefreshError('');
    try {
      await onRefresh();
    } catch {
      setRefreshError('Unable to refresh the paycheck. Retry before paying.');
    } finally {
      setRefreshing(false);
    }
  };
  const payBlocked =
    !!isLoading || !!correcting || adding || refreshing || !!refreshError;
  const selectedEventId = selectedEvent?.id;
  const isPayoutDialogOpen = Boolean(onPay && selectedEventId);
  const paymentMethodId =
    paymentMethod.eventId === selectedEventId ? paymentMethod.id : undefined;

  const { data: paymentMethods, ...paymentMethodsQuery } =
    usePaymentMethodsQuery(
      { status: 'active' },
      {
        skip: !isPayoutDialogOpen,
      }
    );

  const selectedEventPaycheck = data?.find(
    (value) => value.event.id === selectedEventId
  );
  const payoutItemsKey = selectedEventPaycheck?.payoutItems
    .map((item) => `${item.id}:${item.amount}`)
    .join('|');
  const fullPayoutAmount = Math.max(
    Number(selectedEventPaycheck?.totalPayout) || 0,
    0
  );
  const selectedPayoutTotal = Math.min(
    Number(payoutAmount) || 0,
    fullPayoutAmount
  );
  // Typed text is never rewritten: an amount above the full payout is shown
  // as typed, explained, and blocks Pay until it is corrected.
  const amountOverFull =
    Math.round((Number(payoutAmount) || 0) * 100) >
    Math.round(fullPayoutAmount * 100);

  useEffect(() => {
    setPaymentMethod({});
    setPayoutAmount(
      fullPayoutAmount ? String(Number(fullPayoutAmount.toFixed(2))) : ''
    );
  }, [fullPayoutAmount, payoutItemsKey, selectedEventId]);

  const handleClose = () => {
    setCorrecting(undefined);
    setPaymentMethod({});
    setPayoutAmount('');
    onClose();
  };

  const paymentMethodOptions = useMemo(
    () => getPayoutPaymentMethodOptions(paymentMethods),
    [paymentMethods]
  );

  const selectedPaymentMethod = paymentMethodOptions.find(
    (option) => option.value === paymentMethodId
  );

  const selectedPayoutItems = useMemo(
    () =>
      getPayoutItemsForAmount(
        selectedEventPaycheck?.payoutItems,
        selectedPayoutTotal,
        fullPayoutAmount
      ),
    [fullPayoutAmount, selectedEventPaycheck, selectedPayoutTotal]
  );

  const transformedPaychecks = useMemo(
    () =>
      selectedEvent && selectedEventPaycheck
        ? getTransformedPaychecks(selectedEventPaycheck)
        : [],
    [data, selectedEvent, selectedEventPaycheck]
  );

  const displayChargebackAdjustments = useMemo(
    () =>
      buildDisplayChargebackAdjustments(
        selectedEventPaycheck?.chargebackAdjustments
      ),
    [selectedEventPaycheck?.chargebackAdjustments]
  );

  const isPayrollsNegative = selectedPayoutTotal <= 0;

  return (
    <Dialog
      width="520px"
      showCloseButton
      onClose={handleClose}
      open={!!selectedEvent}
      closeOnDocumentClick={false}
      title={`Paycheck ${selectedEvent?.name}`}
    >
      {() => (
        <Loader isFullScreen={false} isLoading={isLoading}>
          <Flex
            gap={8}
            fullWidth
            flexDirection="column"
            style={{
              // Room for the scrollbar outside the rows: an overlay scrollbar
              // (phones, macOS) never covers the amounts, and the rows keep
              // the dialog's own right edge.
              paddingRight: 8,
              marginRight: -8,
              overflowY: 'auto',
              boxSizing: 'border-box',
              width: 'calc(100% + 8px)',
              maxHeight: 'calc(100dvh - 160px)',
            }}
          >
            {transformedPaychecks.map(({ key, name, value }) => {
              const adjustmentTypes: Record<string, Adjustment[] | undefined> =
                {
                  refundTotal: selectedEventPaycheck?.refundAdjustments,
                  chargebackTotal: selectedEventPaycheck?.chargebackAdjustments,
                };

              if (key === 'chargebackTotal') {
                if (selectedEventPaycheck?.chargebackAdjustments?.length) {
                  if (!displayChargebackAdjustments.length) {
                    return null;
                  }
                  return (
                    <SubStyles.SubGroup key={key}>
                      <Card
                        name={name}
                        showPositiveSign
                        value={Number(value)}
                        isRed={Number(value) < 0}
                      />
                      <SubStyles.SubRows>
                        {displayChargebackAdjustments.map((adjustment) => (
                          <SubStyles.SubRow key={adjustment.key}>
                            <span>{adjustment.label}</span>
                            <SubStyles.SubAmount
                              $negative={adjustment.amount < 0}
                            >
                              {adjustment.amount < 0 ? '−' : '+'}
                              {formatPrice(Math.abs(adjustment.amount))}
                            </SubStyles.SubAmount>
                          </SubStyles.SubRow>
                        ))}
                      </SubStyles.SubRows>
                    </SubStyles.SubGroup>
                  );
                }
                return Number(value) ? (
                  <Card
                    key={key}
                    name={name}
                    showPositiveSign
                    value={Number(value)}
                    isRed={Number(value) < 0}
                  />
                ) : null;
              }

              if (Object.keys(adjustmentTypes).includes(key)) {
                return (
                  <AdjustmentCard
                    key={key}
                    name={name}
                    value={Number(value)}
                    adjustments={adjustmentTypes[key]}
                    currentEventName={selectedEvent?.name}
                  />
                );
              }

              const rows =
                selectedEventPaycheck?.payoutItems.filter((item) => {
                  // The automatic broken & missing line has its own row.
                  if (item.payrollRule === 'brokenMissing') {
                    return false;
                  }
                  if (key === 'miscPositiveTotal') {
                    return (
                      item.miscGroup === 'positive' ||
                      (item.type === PayoutType.Misc &&
                        Number(item.amount) >= 0)
                    );
                  }
                  if (key === 'miscNegativeTotal') {
                    return (
                      item.miscGroup === 'negative' ||
                      (item.type === PayoutType.Misc && Number(item.amount) < 0)
                    );
                  }
                  return false;
                }) || [];
              return (
                <div key={key}>
                  <div style={rows.length ? { fontWeight: 600 } : undefined}>
                    <Card
                      key={key}
                      name={name}
                      value={Number(value)}
                      showPositiveSign={key === 'brokenMissingTotal'}
                      titleLevel={
                        boldTypes.includes(key) ? TitleLevel.H4 : TitleLevel.P
                      }
                      isRed={
                        minusTypes.includes(key) ||
                        (key === 'brokenMissingTotal' && Number(value) < 0)
                      }
                    />
                  </div>
                  {!!rows.length && (
                    <ManualPayoutRows
                      rows={rows}
                      onRefresh={refresh}
                      editable={!!onPay && !!onRefresh}
                      onBusy={(busy) => setCorrecting(busy ? key : undefined)}
                      onFailure={() =>
                        setRefreshError(
                          'Refresh the paycheck before paying after a failed correction.'
                        )
                      }
                      disabled={
                        !!isLoading ||
                        refreshing ||
                        adding ||
                        (!!correcting && correcting !== key)
                      }
                    />
                  )}
                </div>
              );
            })}
            {!!employee && (
              <CreateBonusAdvanceDialog
                withReceipt
                isWideDialog
                isDisableEvent
                hideCashFromEvent
                customTitle="Add row"
                showSelectExpensesType
                onSubmittingChange={setAdding}
                employeeId={String(employee?.id)}
                eventId={Number(selectedEvent?.id)}
                customTypeValues={customTypeValues}
                onSuccessSubmit={() => {
                  void refresh();
                }}
                customEmployeeOptions={[
                  {
                    value: String(employee?.id),
                    label: String(employee?.user?.name),
                  },
                ]}
                trigger={
                  <Styles.AddRowButton
                    transparent
                    padding="0"
                    text="Add row"
                    icon={<Icon.Plus />}
                    disabled={payBlocked}
                    colorType={ButtonColors.LightBlue}
                  />
                }
              />
            )}

            {refreshError && (
              <div role="alert">
                {refreshError}
                <button
                  type="button"
                  disabled={refreshing}
                  onClick={() => void refresh()}
                >
                  Retry refresh
                </button>
              </div>
            )}
            {!!onPay && (
              <>
                <Styles.PayoutAmountRow>
                  <Styles.PayoutItemLabel>Amount</Styles.PayoutItemLabel>
                  <Styles.PayoutItemControls>
                    <Styles.PayoutAmountInput
                      inputMode="decimal"
                      aria-label="Amount"
                      value={payoutAmount}
                      onChange={(event) => {
                        const rawAmount = event.target.value.replace(',', '.');
                        if (rawAmount === '') {
                          setPayoutAmount('');
                          return;
                        }
                        // Keep what is typed while it is a valid partial
                        // amount, so "501." and "501.5" survive on the way
                        // to cents; never more than two decimals.
                        if (!/^\d*\.?\d{0,2}$/.test(rawAmount)) {
                          return;
                        }
                        setPayoutAmount(rawAmount);
                      }}
                    />
                    <Button
                      transparent
                      padding="0"
                      text="Full amount"
                      colorType={ButtonColors.LightBlue}
                      onClick={() =>
                        setPayoutAmount(
                          fullPayoutAmount
                            ? String(Number(fullPayoutAmount.toFixed(2)))
                            : ''
                        )
                      }
                    />
                  </Styles.PayoutItemControls>
                </Styles.PayoutAmountRow>
                {amountOverFull && (
                  <Styles.Text isRed role="alert">
                    Can&apos;t be more than the full payout of{' '}
                    {formatPrice(fullPayoutAmount)}.
                  </Styles.Text>
                )}
                {/* The rows scroll inside their own box (overflowY above), so
                    an in-flow menu is clipped at its bottom edge. A fixed menu
                    floats over the dialog, stays inside it (outside-click and
                    Tab keep working) and opens whole: below the field when the
                    full list (300 px) fits there, otherwise above it, so it
                    never runs into the screen edge. */}
                <Select
                  isSearchable
                  requiredText="*"
                  minMenuHeight={300}
                  menuPosition="fixed"
                  label="Payment Method"
                  placeholder="Payment Method"
                  value={selectedPaymentMethod}
                  options={paymentMethodOptions}
                  inputId="paycheck-payment-method"
                  isLoading={paymentMethodsQuery.isLoading}
                  onChange={(option) => {
                    const selected = Array.isArray(option) ? option[0] : option;
                    setPaymentMethod({
                      eventId: selectedEventId,
                      id: Number(selected?.value) || undefined,
                    });
                  }}
                />
                <Button
                  fullWidth
                  text={`Pay (${formatPrice(selectedPayoutTotal)})`}
                  onClick={() =>
                    !payBlocked &&
                    !amountOverFull &&
                    paymentMethodId &&
                    onPay(paymentMethodId, selectedPayoutItems)
                  }
                  disabled={
                    payBlocked ||
                    amountOverFull ||
                    isPayrollsNegative ||
                    !paymentMethodId ||
                    !selectedPayoutItems.length
                  }
                />
              </>
            )}
          </Flex>
        </Loader>
      )}
    </Dialog>
  );
};
